import { Tab, TabList } from '@fluentui/react-components';

import { FILTER_LABELS, FILTER_ORDER, RowFilter } from './identityRowModel';

export interface IdentityStatusTabsProps {
  filter: RowFilter;
  counts: Record<RowFilter, number>;
  onChange: (next: RowFilter) => void;
}

/**
 * FD-384 — the actionable rows are the landing view. `Needs action` is the
 * default, so opening the workspace shows the work rather than a list dominated
 * by rows that are already done.
 *
 * FD-508 made these a real `TabList` rather than the row of toggle buttons they
 * were. Six mutually-exclusive views of one dataset is exactly what a tab strip
 * means, and buttons styled to look like tabs would have announced as something
 * they are not. The consequence is that they carry `role="tab"` and
 * `aria-selected` where they carried `role="button"` and `aria-pressed`, which
 * is a real change to how they are addressed and is recorded as such.
 *
 * The counts are over ALL rows, never the filtered set: a count that moved with
 * the active tab would be useless for deciding where to go next. That is a
 * property of what the caller passes in, and it has a test.
 */
export default function IdentityStatusTabs({
  filter,
  counts,
  onChange,
}: IdentityStatusTabsProps) {
  // The rule under the strip belongs to the CALLER's controls row, not to this
  // component: it has to span the toolbar sitting beside the tabs, and a border
  // declared here stops where the last tab stops. Restoring one to this element
  // draws a second, shorter rule on top of that one.
  return (
    <TabList
      selectedValue={filter}
      onTabSelect={(_, data) => onChange(data.value as RowFilter)}
      aria-label="Filter mappings"
    >
      {FILTER_ORDER.map((f) => (
        <Tab key={f} value={f}>
          {FILTER_LABELS[f]} ({counts[f] ?? 0})
        </Tab>
      ))}
    </TabList>
  );
}
